import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { reportErrorToFaro } from '@sendible/common';
import { useToastNotificationContext } from '@sendible/design-system';
import { NotificationTypeEnum } from '@sendible/design-system/src/components/molecules/Toast';
import { useTranslation } from 'react-i18next';
import { useCalendarContext } from './context';

// Multiple views can have selectable posts on screen at once (e.g. the monthly grid
// behind an open day drawer, which loads its own fuller list for that one day) — each
// reports its own visible ids under its own key, and "select all" is the union of them.
export const visibleMessageSources = ['monthly-grid', 'weekly-grid', 'daily-grid', 'day-sidebar'] as const;
export type VisibleMessageSource = (typeof visibleMessageSources)[number];

// A view that knows its true bounded total (e.g. the monthly grid, from the summary
// endpoint's per-day counts) can register one of these so "select all" fetches and selects
// every post in that scope, not just the ones currently rendered/truncated on screen.
export type SelectAllCapability = {
  fetchAllIds: (signal?: AbortSignal) => Promise<CalendarMessage['id'][]>;
  totalCount: number;
};

type SelectionContextType = {
  clearVisibleMessageIds: (source: VisibleMessageSource) => void;
  deselectAll: () => void;
  isSelectAllActive: boolean;
  isSelected: (id: CalendarMessage['id']) => boolean;
  isSelectingAll: boolean;
  maxItemsCount: number;
  removeFromSelection: (ids: CalendarMessage['id'][]) => void;
  selectAll: () => void;
  selectedMessageIds: CalendarMessage['id'][];
  setSelectAllCapability: (capability: SelectAllCapability | null) => void;
  setVisibleMessageIds: (source: VisibleMessageSource, ids: CalendarMessage['id'][]) => void;
  toggleMessage: (id: CalendarMessage['id']) => void;
};

export const SelectionContext = createContext({} as SelectionContextType);

export const SelectionProvider = ({ children }: Component) => {
  const { currentDate, filterParams, viewMode } = useCalendarContext();
  const [searchParams] = useSearchParams();
  const activeView = searchParams.get('view');
  const { t } = useTranslation('calendar');
  const { showToastNotification } = useToastNotificationContext();
  const [selectedMessageIds, setSelectedMessageIds] = useState<CalendarMessage['id'][]>([]);
  const [visibleMessagesBySource, setVisibleMessagesBySource] = useState<Partial<Record<VisibleMessageSource, CalendarMessage['id'][]>>>({});
  // Tracks whether the current selection is "everything visible" (set by selectAll, cleared
  // by any individual deselect or deselectAll) — read via a ref inside the stable
  // setVisibleMessageIds callback so opening a day's drawer can top up an active
  // select-all with that day's extra posts without needing setVisibleMessageIds itself
  // to depend on it (which would break its referential stability for effect consumers).
  const [isSelectAllActive, setIsSelectAllActive] = useState(false);
  const isSelectAllActiveRef = useRef(isSelectAllActive);

  useEffect(() => {
    isSelectAllActiveRef.current = isSelectAllActive;
  }, [isSelectAllActive]);

  const deselectAll = useCallback(() => {
    setSelectedMessageIds([]);
    setIsSelectAllActive(false);
  }, []);

  const [selectAllCapability, setSelectAllCapability] = useState<SelectAllCapability | null>(null);
  const [isSelectingAll, setIsSelectingAll] = useState(false);

  // Selection is scoped to whatever's currently on screen — reset whenever the user
  // switches view mode or main/scheduled/sent/failed tab, and (monthly/weekly only) when
  // they navigate to a different date via prev/next/today. The daily/list view also
  // updates `currentDate` continuously as a scroll anchor while the user scrolls past a
  // month boundary — that's not a deliberate navigation, so it's excluded here, or every
  // scroll tick would silently wipe the selection.
  // Also reset on filterParams changing — otherwise a selection made under one filter (e.g.
  // a channel/tag filter) can survive into a different filtered view, where some of the
  // selected ids are no longer even on screen.
  // Deliberately does NOT watch sidebar open/close, so selection survives the drawer.
  const filterParamsKey = JSON.stringify(filterParams ?? {});
  const resetKey =
    viewMode === 'daily' ? `${viewMode}|${activeView}|${filterParamsKey}` : `${currentDate}|${viewMode}|${activeView}|${filterParamsKey}`;
  const [prevResetKey, setPrevResetKey] = useState(resetKey);

  // Guards a select-all fetch against resolving into a scope it was no longer started for
  // (e.g. the user navigates to a different month while "select all" for the old month is
  // still in flight) — bumped whenever resetKey changes and at the start of every selectAll()
  // call, and checked before committing that call's result.
  const selectAllEpochRef = useRef(0);
  const selectAllAbortControllerRef = useRef<AbortController | null>(null);

  if (resetKey !== prevResetKey) {
    setPrevResetKey(resetKey);
    setSelectedMessageIds([]);
    setIsSelectAllActive(false);
    setIsSelectingAll(false);
  }

  // Ref mutations can't happen during render (see the state reset above, which is the render-safe
  // part of this same reset) — so the epoch bump/abort itself runs as an effect keyed on resetKey.
  useEffect(() => {
    selectAllEpochRef.current += 1;
    selectAllAbortControllerRef.current?.abort();
    selectAllAbortControllerRef.current = null;
  }, [resetKey]);

  const toggleMessage = useCallback(
    (id: CalendarMessage['id']) => {
      if (selectedMessageIds.includes(id)) setIsSelectAllActive(false);
      setSelectedMessageIds((prev) => (prev.includes(id) ? prev.filter((existingId) => existingId !== id) : [...prev, id]));
    },
    [selectedMessageIds]
  );

  const isSelected = useCallback((id: CalendarMessage['id']) => selectedMessageIds.includes(id), [selectedMessageIds]);

  const setVisibleMessageIds = useCallback((source: VisibleMessageSource, ids: CalendarMessage['id'][]) => {
    setVisibleMessagesBySource((prev) => ({ ...prev, [source]: ids }));

    // Opening a day's drawer is one deliberate, bounded action — top up an active "select
    // all" with its extra posts. Deliberately scoped to the drawer only: the daily/list
    // view's own visible ids keep growing as the user scrolls (unbounded, not a deliberate
    // action), and auto-selecting more posts as a side effect of scrolling would be a
    // surprising way to grow a *delete* selection.
    if (source === 'day-sidebar' && isSelectAllActiveRef.current) {
      setSelectedMessageIds((prevSelected) => {
        const newlyVisible = ids.filter((id) => !prevSelected.includes(id));

        return newlyVisible.length > 0 ? [...prevSelected, ...newlyVisible] : prevSelected;
      });
    }
  }, []);

  const clearVisibleMessageIds = useCallback((source: VisibleMessageSource) => {
    setVisibleMessagesBySource((prev) => {
      if (!(source in prev)) return prev;

      const next = { ...prev };

      delete next[source];

      return next;
    });
  }, []);

  const visibleMessageIds = useMemo(() => Array.from(new Set(Object.values(visibleMessagesBySource).flat())), [visibleMessagesBySource]);

  const maxItemsCount = selectAllCapability ? selectAllCapability.totalCount : visibleMessageIds.length;

  const removeFromSelection = useCallback((ids: CalendarMessage['id'][]) => {
    if (ids.length === 0) return;

    setSelectedMessageIds((prev) => prev.filter((existingId) => !ids.includes(existingId)));
  }, []);

  const selectAll = useCallback(async () => {
    if (!selectAllCapability) {
      setSelectedMessageIds(visibleMessageIds);
      setIsSelectAllActive(true);

      return;
    }

    const epoch = ++selectAllEpochRef.current;
    const controller = new AbortController();

    selectAllAbortControllerRef.current = controller;
    setIsSelectingAll(true);

    try {
      const ids = await selectAllCapability.fetchAllIds(controller.signal);

      // The scope this fetch was started for (view/date/tab/filter) may have moved on while
      // it was in flight — a stale result must never overwrite whatever the user selected since.
      if (selectAllEpochRef.current !== epoch) return;

      setSelectedMessageIds(ids);
      setIsSelectAllActive(true);
    } catch (error) {
      // An abort here means the scope changed and selectAll (or the resetKey guard) already
      // cancelled this request deliberately — not a real failure, so no error surfaced.
      if (controller.signal.aborted) return;

      reportErrorToFaro(error, 'SelectionContext selectAll');
      showToastNotification({
        hideTimeout: 3000,
        notificationText: t('select_all_error') as string,
        type: NotificationTypeEnum.Error,
        showCloseButton: true,
      });
    } finally {
      if (selectAllEpochRef.current === epoch) setIsSelectingAll(false);
    }
  }, [selectAllCapability, visibleMessageIds, showToastNotification, t]);

  const value = useMemo(
    () => ({
      clearVisibleMessageIds,
      deselectAll,
      isSelectAllActive,
      isSelected,
      isSelectingAll,
      maxItemsCount,
      removeFromSelection,
      selectAll,
      selectedMessageIds,
      setSelectAllCapability,
      setVisibleMessageIds,
      toggleMessage,
    }),
    [
      clearVisibleMessageIds,
      deselectAll,
      isSelectAllActive,
      isSelected,
      isSelectingAll,
      maxItemsCount,
      removeFromSelection,
      selectAll,
      selectedMessageIds,
      setVisibleMessageIds,
      toggleMessage,
    ]
  );

  return <SelectionContext.Provider value={value}>{children}</SelectionContext.Provider>;
};

export const useSelectionContext = (): SelectionContextType => useContext(SelectionContext);
